import { API_CONFIG } from '@/config/api'
import { ENV } from '@/config/environment'
import { ApiError, baseService } from './baseService'
import type { 
  Project, 
  CreateProjectRequest, 
  UpdateProjectRequest,
  ProjectParticipant,
  CreateProjectParticipantRequest,
  Study,
  CreateStudyRequest,
  UpdateStudyRequest
} from './projectTypes'

interface SendInvitationRequest {
  email: string
  projectId: number
  projectName: string
}

export interface StudyDashboardRespondent {
  id: number
  status: 'invited' | 'assigned' | 'in_progress' | 'completed' | 'cancelled' | 'abandoned'
  started_at: string | null
  completed_at: string | null
  duration_seconds: number | null
  has_recording: boolean
  /** Face still captured at record time (survives RODO video deletion); shown as
   *  the respondent's avatar. Null when no thumbnail was captured/stored. */
  thumbnail_url: string | null
  /** Detected from the User-Agent at session begin; '' when the respondent never began. */
  device: 'mobile' | 'desktop' | ''
  demographics: Record<string, string>
  is_anonymous: boolean
  /** Real email/name for invited respondents; blank for anonymous walk-ins. */
  display_name: string
}

export interface StudyDashboard {
  counts: {
    invited: number
    started: number
    completed: number
    in_progress: number
    cancelled: number
    viewed: number
  }
  target_respondents: number
  remaining_to_target: number | null
  avg_duration_seconds: number | null
  today_completed: number
  funnel: Array<{ key: string; n: number }>
  heatmap: number[]
  activity: Array<{ id: string; kind: string; who: string; target?: string; at: string }>
  respondents: StudyDashboardRespondent[]
}

export interface QuestionnaireTemplate {
  id: number
  name: string
  instructions: string | null
  interpretation: string | null
  /** True for per-study questionnaires auto-created by the wizard; hidden from the templates library. */
  is_hidden?: boolean
  questions_count: number
  created_at?: string
  updated_at?: string
}

interface Question {
  id: number
  text: string
  question_type: 'TEXT' | 'SINGLE_CHOICE' | 'MULTIPLE_CHOICE'
  section_name: string
  choices: Array<{
    value: number
    text: string
    skip_questions?: number
  }>
}

export class ProjectService {
  async getProjects(): Promise<Project[]> {
    const response = await baseService.get<{ results: Project[] }>(API_CONFIG.ENDPOINTS.PROJECTS.LIST)
    return response.data.results
  }

  async getProject(id: number): Promise<Project> {
    const response = await baseService.get<Project>(API_CONFIG.ENDPOINTS.PROJECTS.DETAIL(id.toString()))
    return response.data
  }

  async createProject(projectData: CreateProjectRequest): Promise<Project> {
    const response = await baseService.post<Project>(API_CONFIG.ENDPOINTS.PROJECTS.LIST, projectData)
    return response.data
  }

  async updateProject(id: number, projectData: UpdateProjectRequest): Promise<Project> {
    const response = await baseService.put<Project>(API_CONFIG.ENDPOINTS.PROJECTS.DETAIL(id.toString()), projectData)
    return response.data
  }

  async deleteProject(id: number): Promise<void> {
    await baseService.delete(API_CONFIG.ENDPOINTS.PROJECTS.DETAIL(id.toString()))
  }

  async getProjectParticipants(projectId: number): Promise<ProjectParticipant[]> {
    const response = await baseService.get<ProjectParticipant[]>(`${API_CONFIG.ENDPOINTS.PROJECTS.DETAIL(projectId.toString())}participants/`)
    return response.data
  }

  async addParticipant(projectId: number, participantData: CreateProjectParticipantRequest): Promise<ProjectParticipant> {
    const response = await baseService.post<ProjectParticipant>(
      `${API_CONFIG.ENDPOINTS.PROJECTS.DETAIL(projectId.toString())}/add_participant/`,
      participantData
    )
    return response.data
  }

  async uploadStimulus(
    projectId: number,
    data: {
      type: string
      title?: string
      order?: number
      exposureMs?: number
      file?: File | null
      storageUrl?: string
      payload?: string
      /** Type-specific settings — for a Figma prototype, the goal screen. */
      config?: Record<string, unknown>
    },
  ): Promise<import('./projectTypes').ProjectStimulus> {
    const form = new FormData()
    form.append('type', data.type)
    if (data.title) form.append('title', data.title)
    if (data.order != null) form.append('order', String(data.order))
    if (data.exposureMs != null) form.append('exposure_ms', String(data.exposureMs))
    if (data.storageUrl) form.append('storage_url', data.storageUrl)
    if (data.payload) form.append('payload', data.payload)
    if (data.file) form.append('file', data.file)
    if (data.config) form.append('config', JSON.stringify(data.config))

    const token = localStorage.getItem('access_token')
    const url = `${ENV.API_BASE_URL}${API_CONFIG.ENDPOINTS.PROJECTS.DETAIL(projectId.toString())}stimuli/`
    const response = await fetch(url, {
      method: 'POST',
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: form,
    })
    if (!response.ok) {
      const err = await response.json().catch(() => ({}))
      // Carry the machine-readable code through: a stimulus can be refused for
      // reasons the wizard has to explain differently (too long, content the
      // moderation classifier rejected), and a bare message string loses that.
      throw new ApiError(
        err.error || err.detail || `Failed to upload stimulus (${response.status})`,
        response.status,
        response.statusText,
        err,
        typeof err.code === 'string' ? err.code : undefined,
      )
    }
    return response.json()
  }

  /** Remove one stimulus from a project. The wizard needs this so a customer
   *  who swaps their material does not end up with both on the study: autosave
   *  uploads a file the moment it is picked, so dropping it from local state
   *  alone would leave the first upload attached. */
  async deleteStimulus(projectId: number, stimulusId: number): Promise<void> {
    await baseService.delete(
      `${API_CONFIG.ENDPOINTS.PROJECTS.DETAIL(projectId.toString())}stimuli/${stimulusId}/`,
    )
  }

  async getStudyDashboard(projectId: number): Promise<StudyDashboard> {
    const response = await baseService.get<StudyDashboard>(
      `${API_CONFIG.ENDPOINTS.PROJECTS.DETAIL(projectId.toString())}study-dashboard/`,
    )
    return response.data
  }

  async getQuestionnaireResponses(projectId: number): Promise<any[]> {
    const response = await baseService.get<any[]>(`${API_CONFIG.ENDPOINTS.PROJECTS.DETAIL(projectId.toString())}questionnaire_responses/`)
    return response.data
  }

  async setProjectQuestionnaire(projectId: number, templateId: number | null): Promise<Project> {
    return this.updateProject(projectId, { questionnaire_template: templateId })
  }

  async sendInvitation(data: SendInvitationRequest): Promise<{ message: string, participant: ProjectParticipant }> {
    const response = await baseService.post<{ message: string, participant: ProjectParticipant }>(API_CONFIG.ENDPOINTS.INVITATIONS.SEND, {
      email: data.email,
      project_id: data.projectId,
      project_name: data.projectName
    })
    return response.data
  }

  async getQuestionnaireTemplates(): Promise<QuestionnaireTemplate[]> {
    const response = await baseService.get<{ results: QuestionnaireTemplate[] }>(API_CONFIG.ENDPOINTS.QUESTIONNAIRE_TEMPLATES.LIST)
    return response.data.results
  }

  async getQuestionnaireTemplate(id: number): Promise<QuestionnaireTemplate & { questions: Question[] }> {
    const response = await baseService.get<QuestionnaireTemplate & { questions: Question[] }>(
      API_CONFIG.ENDPOINTS.QUESTIONNAIRE_TEMPLATES.DETAIL(id)
    )
    return response.data
  }

  async createQuestionnaireTemplate(data: Omit<QuestionnaireTemplate & { questions: Question[] }, 'id' | 'questions_count'>): Promise<QuestionnaireTemplate> {
    const response = await baseService.post<QuestionnaireTemplate>(
      API_CONFIG.ENDPOINTS.QUESTIONNAIRE_TEMPLATES.LIST,
      data
    )
    return response.data
  }

  async updateQuestionnaireTemplate(id: number, data: Omit<QuestionnaireTemplate & { questions: Question[] }, 'id' | 'questions_count'>): Promise<QuestionnaireTemplate> {
    const response = await baseService.put<QuestionnaireTemplate>(
      API_CONFIG.ENDPOINTS.QUESTIONNAIRE_TEMPLATES.DETAIL(id),
      data
    )
    return response.data
  }

  async deleteQuestionnaireTemplate(id: number): Promise<void> {
    await baseService.delete(API_CONFIG.ENDPOINTS.QUESTIONNAIRE_TEMPLATES.DETAIL(id))
  }

  async getProjectStudies(projectId: number): Promise<Study[]> {
    const response = await baseService.get<{ results?: Study[] } | Study[]>(
      `${API_CONFIG.ENDPOINTS.PROJECTS.DETAIL(projectId.toString())}studies/`
    )
    const data = response.data
    return Array.isArray(data) ? data : (data.results ?? [])
  }

  async createProjectStudy(projectId: number, data: CreateStudyRequest): Promise<Study> {
    const response = await baseService.post<Study>(
      `${API_CONFIG.ENDPOINTS.PROJECTS.DETAIL(projectId.toString())}studies/`,
      data
    )
    return response.data
  }

  async updateStudy(studyId: number, data: UpdateStudyRequest): Promise<Study> {
    const response = await baseService.patch<Study>(
      `/studies/${studyId}/`,
      data
    )
    return response.data
  }

  async deleteStudy(studyId: number): Promise<void> {
    await baseService.delete(`/studies/${studyId}/`)
  }

  async exportProjectAnalysisZip(
    projectId: number,
    projectParticipantIds: number[],
  ): Promise<{ blob: Blob; filename: string }> {
    const params = new URLSearchParams()
    params.set('participant_ids', projectParticipantIds.join(','))
    const url = `${ENV.API_BASE_URL}${API_CONFIG.ENDPOINTS.PROJECTS.EXPORT_ANALYSIS(projectId)}?${params.toString()}`
    const token = localStorage.getItem('access_token')

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      const message =
        (typeof errorData.detail === 'string' && errorData.detail) ||
        (typeof errorData.error === 'string' && errorData.error) ||
        `Failed to export analysis data: ${response.statusText}`
      throw new Error(message)
    }

    const blob = await response.blob()
    const disposition = response.headers.get('Content-Disposition') || ''
    const filenameMatch = disposition.match(/filename="?([^"]+)"?/)
    const filename = filenameMatch?.[1] || `project_${projectId}_analysis_export.zip`
    return { blob, filename }
  }
}

export const projectService = new ProjectService()